import { Dispatch } from "redux";
import { Action, ActionType } from "../actions";
import PluginService from "../../services/pluginService";
import { IPluginConfiguration } from "../../models/configuration/plugin/IConfiguration";
import EncompassService from "../../services/encompassService";
import { hasPluginConfigChanges } from "../../utils/pluginConfigChangeDetector";
import { syncDisclosureTrackingToPushback } from "./syncPushbackHelper";

// This action creator gets the server config values from an api call or state
const saveConfigPlugInData = (configId: string, plugInData: IPluginConfiguration, token: string | null) => {
    return async (dispatch: Dispatch<Action>, getState: () => any) => {

        dispatch({ type: ActionType.SAVE_CONFIG_PLUGIN_DATA });

        try {
            const { persistedData, pushbackSyncPending } = getState().configPlugInData;

            const updatedConfig = {
                ...plugInData.Configuration,
                OtherControls: [...plugInData.Configuration.OtherControls],
                Packages: [...plugInData.Configuration.Packages],
                UseExpressionCaching: plugInData.Configuration.UseExpressionCaching,
                DeliveryTypes: [...plugInData.Configuration.DeliveryTypes],
                AllowMultiDelivery: plugInData.Configuration.AllowMultiDelivery,
                Impersonation: { ...plugInData.Configuration.Impersonation },
                SendValidations: [...plugInData.Configuration.SendValidations],
                ErrorMessageOverrides: [...plugInData.Configuration.ErrorMessageOverrides],
                DefaultValues: { ...plugInData.Configuration.DefaultValues },
                DisclosureTrackingMapping: { ...plugInData.Configuration.DisclosureTrackingMapping },
                FormToolTips: [...plugInData.Configuration.FormToolTips],
                EnableTpo: plugInData.Configuration.EnableTpo,
            };

            // Check if Plugin has changes
            const hasPluginChanges = hasPluginConfigChanges(updatedConfig, persistedData?.Configuration);

            // If no plugin changes AND no pending pushback sync, nothing to do
            if (!hasPluginChanges && !pushbackSyncPending) {
                dispatch({
                    type: ActionType.SAVE_CONFIG_PLUGIN_DATA_SUCCESS,
                    payload: { "Configuration": updatedConfig }
                });
                return false;
            }

            // 1. Save to Plugin API (only if Plugin has changes)
            let configToSave = updatedConfig;
            if (hasPluginChanges) {
                configToSave = {
                    ...updatedConfig,
                    LastUpdateUtc: new Date().toISOString()
                };

                const requestBody = {
                    "ConfigurationId": configId,
                    "EncompassInstanceId": EncompassService.getEncompassInstanceId(),
                    "Configuration": configToSave
                }

                await new PluginService().saveEpcApiConfig<IPluginConfiguration>(token, "plugin", configId, requestBody);

                dispatch({
                    type: ActionType.SAVE_CONFIG_PLUGIN_DATA_SUCCESS,
                    payload: { "Configuration": configToSave }
                });
                dispatch({
                    type: ActionType.SAVE_CONFIG_PLUGIN_DATA_PERSISTED,
                    payload: { "Configuration": configToSave }
                });
            }

            // 2. Sync Disclosure Tracking Mapping to Pushback API
            // Always sync if: Plugin just saved OR previous Pushback sync failed
            if (configToSave.DisclosureTrackingMapping && (hasPluginChanges || pushbackSyncPending)) {
                const syncSucceeded = await syncDisclosureTrackingToPushback(
                    dispatch,
                    token,
                    configId,
                    configToSave.DisclosureTrackingMapping
                );

                // If sync failed, return true to indicate Plugin saved but retry needed
                if (!syncSucceeded) {
                    return true;
                }
            }

            return true;

        } catch (error: any) {
            const payloadErrorMessage = `Failed to save packages data: (${error.message})`;
            dispatch({
                type: ActionType.SAVE_CONFIG_PLUGIN_DATA_FAILURE,
                payload: payloadErrorMessage,
            });

            return Promise.reject(payloadErrorMessage);
        }
    }
}

export default saveConfigPlugInData;